/**
 * DBN Project Portfolio — Command Centre
 * Executive Intelligence + Impact/Risk Matrix + Watchlist + AI Insights + Existing Table
 */
import React, { useState, useEffect, Suspense } from "react";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip as RTooltip,
  ResponsiveContainer, Cell, BarChart, Bar, CartesianGrid, Legend
} from "recharts";
import { dbnApi, PageHeader, SectionHeader, Badge, LoadingState, fmtEur, fmtN, fmtPct,
  scoreColor, riskColor, ProgressBar, KPICard } from "./dbnUtils";
import DBNNigeriaMap from "./DBNNigeriaMap";
import { TrendingUp, AlertTriangle, Target, Eye, DollarSign, Users, Leaf, ChevronRight, Search } from "lucide-react";

// ── Colour palette consistent with platform ──────────────────────────────────
const SECTOR_COLORS = {
  "Renewable Energy":"#34d399","Agriculture":"#60a5fa","Clean Transportation":"#a78bfa",
  "Circular Economy":"#fbbf24","Green Manufacturing":"#f97316","Water and Sanitation":"#0891b2",
  "Healthcare":"#ec4899","SME Recovery":"#6b7280","Clean Energy":"#0d9488",
};
const STAGE_COLORS = {
  "Monitoring":"#22c55e","Disbursed":"#0d9488","Approved":"#3b82f6",
  "Due Diligence":"#f97316","PFI Review":"#f59e0b","Technical Assistance":"#a78bfa",
  "Readiness Assessment":"#475569",
};

// ── Treemap (custom SVG) ──────────────────────────────────────────────────────
function SimpleTreemap({ data, width = 500, height = 160, colorKey }) {
  if (!data || !data.length) return null;
  const total = data.reduce((a, d) => a + d.finance, 0);
  let x = 0;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
      {data.map((d, i) => {
        const w = Math.max((d.finance / total) * width, 2);
        const color = colorKey ? (SECTOR_COLORS[d[colorKey]] || "#475569") : SECTOR_COLORS[d.sector] || "#475569";
        const rect = { x, y: 0, w, h: height };
        x += w;
        return (
          <g key={d.sector || d.state || i}>
            <rect x={rect.x} y={rect.y} width={rect.w - 1} height={rect.h}
              fill={color} fillOpacity={0.85} rx={3} />
            {rect.w > 50 && (
              <text x={rect.x + rect.w/2} y={rect.h/2 - 4} textAnchor="middle"
                fill="#fff" fontSize={9} fontWeight={700}>
                {d.sector || d.state}
              </text>
            )}
            {rect.w > 50 && (
              <text x={rect.x + rect.w/2} y={rect.h/2 + 10} textAnchor="middle"
                fill="rgba(255,255,255,0.75)" fontSize={8}>
                {fmtEur(d.finance)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ── Bubble Chart Tooltip ──────────────────────────────────────────────────────
function BubbleTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div style={{ background:"#0a1628", border:"1px solid #1e3a5f", borderRadius:10,
      padding:"10px 14px", fontSize:10, maxWidth:200 }}>
      <p style={{ fontWeight:700, color:"#f0f9ff", marginBottom:4 }}>{d.name}</p>
      <p style={{ color:"#60a5fa" }}>Finance: {fmtEur(d.finance)}</p>
      <p style={{ color:"#94a3b8" }}>Risk Score: {d.x}</p>
      <p style={{ color:"#34d399" }}>Impact Score: {d.y}</p>
      <p style={{ color:riskColor(d.risk) }}>Risk: {d.risk}</p>
      <p style={{ color:"#a78bfa" }}>Stage: {d.stage}</p>
    </div>
  );
}

// ── Executive Watchlist ───────────────────────────────────────────────────────
function WatchlistCard({ item, onOpen }) {
  return (
    <div onClick={() => onOpen(item)} style={{
      background:"#060f1e", border:"1px solid #0f2033", borderRadius:10,
      padding:"9px 12px", cursor:"pointer", display:"flex", alignItems:"center", gap:10,
      transition:"all .15s", marginBottom:6
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor=item.color; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor="#0f2033"; }}>
      <div style={{ width:4, height:36, borderRadius:2, background:item.color, flexShrink:0 }} />
      <div style={{ flex:1 }}>
        <p style={{ fontSize:10, fontWeight:700, color:"#f0f9ff", marginBottom:1 }}>{item.name}</p>
        <div style={{ display:"flex", gap:5 }}>
          <Badge label={item.reason} color={item.color} />
          <span style={{ fontSize:8, color:"#475569" }}>{item.sector} · {item.stage}</span>
        </div>
      </div>
      <div style={{ textAlign:"right" }}>
        <p style={{ fontSize:10, fontWeight:700, color:"#34d399" }}>{fmtEur(item.finance)}</p>
        <ChevronRight size={10} color="#334155" />
      </div>
    </div>
  );
}

// ── Project Row ───────────────────────────────────────────────────────────────
function ProjectRow({ project, onClick, selected }) {
  const rc = riskColor(project.risk_rating);
  return (
    <tr onClick={() => onClick(project)}
      style={{ cursor:"pointer", background:selected?"rgba(52,211,153,0.06)":"transparent" }}
      onMouseEnter={e=>{ if(!selected) e.currentTarget.style.background="rgba(255,255,255,0.02)"; }}
      onMouseLeave={e=>{ if(!selected) e.currentTarget.style.background="transparent"; }}>
      <td style={{ padding:"8px 10px", borderBottom:"1px solid #0f1e33" }}>
        <p style={{ fontSize:9.5, fontWeight:700, color:"#f0f9ff" }}>{project.name}</p>
        <p style={{ fontSize:8, color:"#475569" }}>{project.sponsor}</p>
      </td>
      <td style={{ padding:"8px 10px", borderBottom:"1px solid #0f1e33" }}>
        <span style={{ fontSize:8, background:`${SECTOR_COLORS[project.sector]||"#475569"}20`,
          color:SECTOR_COLORS[project.sector]||"#475569", padding:"2px 6px", borderRadius:4, fontWeight:700 }}>
          {project.sector}
        </span>
      </td>
      <td style={{ padding:"8px 10px", fontSize:9, color:"#64748b", borderBottom:"1px solid #0f1e33" }}>{project.state}</td>
      <td style={{ padding:"8px 10px", fontSize:10, color:"#34d399", fontWeight:700, borderBottom:"1px solid #0f1e33" }}>
        {fmtEur(project.finance_amount)}
      </td>
      <td style={{ padding:"8px 10px", borderBottom:"1px solid #0f1e33" }}>
        <Badge label={project.stage} color={STAGE_COLORS[project.stage]||"#475569"} />
      </td>
      <td style={{ padding:"8px 10px", fontSize:9, color:rc, fontWeight:600, borderBottom:"1px solid #0f1e33" }}>{project.risk_rating}</td>
      <td style={{ padding:"8px 10px", fontSize:10, color:scoreColor(project.readiness_score), fontWeight:700, borderBottom:"1px solid #0f1e33" }}>
        {project.readiness_score}
      </td>
      <td style={{ padding:"8px 10px", borderBottom:"1px solid #0f1e33" }}>
        <div style={{ display:"flex", gap:3 }}>
          {project.green_eligible   ? <span style={{ fontSize:7, background:"#14532d", color:"#4ade80", padding:"1px 5px", borderRadius:3, fontWeight:700 }}>G</span> : null}
          {project.digital_eligible ? <span style={{ fontSize:7, background:"#1e3a5f", color:"#60a5fa", padding:"1px 5px", borderRadius:3, fontWeight:700 }}>D</span> : null}
          {project.inclusion_eligible ? <span style={{ fontSize:7, background:"#2d1b69", color:"#a78bfa", padding:"1px 5px", borderRadius:3, fontWeight:700 }}>I</span> : null}
        </div>
      </td>
    </tr>
  );
}

// ── Project Workspace Drawer ───────────────────────────────────────────────────
function ProjectDetail({ project, onClose }) {
  const [tab, setTab] = useState("overview");
  if (!project) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.75)", zIndex:2000,
      display:"flex", alignItems:"flex-start", justifyContent:"flex-end" }} onClick={onClose}>
      <div style={{ width:"100%", maxWidth:560, height:"100%", background:"#0a1628",
        border:"1px solid #1e3a5f", overflowY:"auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ background:"#07111e", borderBottom:"1px solid #0f2033", padding:"14px 20px",
          display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <p style={{ fontSize:13, fontWeight:800, color:"#f0f9ff" }}>{project.name}</p>
            <p style={{ fontSize:10, color:"#475569" }}>{project.id} · {project.sponsor}</p>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"#475569", cursor:"pointer", fontSize:18 }}>✕</button>
        </div>
        <div style={{ display:"flex", borderBottom:"1px solid #0f2033", overflowX:"auto" }}>
          {["overview","financial","impact","carbon","risk","timeline"].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding:"8px 14px", border:"none", borderBottom:`2px solid ${tab===t?"#34d399":"transparent"}`,
                background:"transparent", color:tab===t?"#34d399":"#475569", fontSize:10, fontWeight:700,
                cursor:"pointer", textTransform:"capitalize", whiteSpace:"nowrap" }}>
              {t}
            </button>
          ))}
        </div>
        <div style={{ padding:"16px 20px" }}>
          {tab === "overview" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[
                ["Project ID",project.id],["Stage",project.stage],["Status",project.status],
                ["Sector",project.sector],["Subsector",project.subsector],["State / LGA",`${project.state} / ${project.lga}`],
                ["Project Type",project.project_type],["PFI",project.pfi_name||project.pfi_id],
                ["Facility",project.facility_id],["ESG Category",project.esg_category],
                ["Risk Rating",project.risk_rating],["Readiness",`${project.readiness_score}/100`],
                ["Green",project.green_eligible?"Yes":"No"],["Digital",project.digital_eligible?"Yes":"No"],
                ["Inclusion",project.inclusion_eligible?"Yes":"No"],
              ].map(([l,v]) => (
                <div key={l} style={{ background:"#060f1e", borderRadius:6, padding:"7px 10px" }}>
                  <p style={{ fontSize:8, color:"#475569", marginBottom:1 }}>{l}</p>
                  <p style={{ fontSize:10, fontWeight:600, color:"#f0f9ff" }}>{v}</p>
                </div>
              ))}
            </div>
          )}
          {tab === "financial" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[
                ["Finance Amount",fmtEur(project.finance_amount)],
                ["Total Project Cost",fmtEur(project.project_cost)],
                ["Sponsor Equity",fmtEur(project.sponsor_equity)],
                ["Debt",fmtEur(project.debt)],
                ["Guarantee",fmtEur(project.guarantee_amount)],
                ["IRR",`${project.irr}%`],
                ["DSCR",`${project.dscr}x`],
                ["Payback",`${project.payback_yrs} yr`],
                ["Mobilisation",`${project.mobilisation_ratio}x`],
                ["Private Co-finance",fmtEur((project.project_cost||0)-(project.finance_amount||0))],
              ].map(([l,v]) => (
                <div key={l} style={{ background:"#060f1e", borderRadius:6, padding:"7px 10px" }}>
                  <p style={{ fontSize:8, color:"#475569", marginBottom:1 }}>{l}</p>
                  <p style={{ fontSize:11, fontWeight:700, color:"#34d399" }}>{v}</p>
                </div>
              ))}
            </div>
          )}
          {tab === "impact" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[
                ["Jobs Created",project.jobs_created?.toLocaleString()],
                ["Women Beneficiaries",project.women_count?.toLocaleString()],
                ["Youth Beneficiaries",project.youth_count?.toLocaleString()],
                ["Rural Beneficiaries",project.rural_beneficiaries?.toLocaleString()],
                ["Emissions Avoided",`${project.emissions_avoided_tco2e?.toLocaleString()} tCO₂e`],
                ["Energy Generated",`${project.energy_mwh?.toLocaleString()} MWh`],
                ["Waste Recovered",`${project.waste_recovered_t?.toLocaleString()} t`],
                ["Digital Users",project.digital_users?.toLocaleString()],
              ].map(([l,v]) => (
                <div key={l} style={{ background:"#060f1e", borderRadius:6, padding:"7px 10px" }}>
                  <p style={{ fontSize:8, color:"#475569", marginBottom:1 }}>{l}</p>
                  <p style={{ fontSize:11, fontWeight:700, color:"#60a5fa" }}>{v||"—"}</p>
                </div>
              ))}
            </div>
          )}
          {tab === "carbon" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[
                ["Carbon Eligible",project.carbon_eligible?"Yes":"No"],
                ["Plastic Eligible",project.plastic_eligible?"Yes":"No"],
                ["Carbon Credits/yr",`${project.carbon_credits_annual?.toLocaleString()} VCU`],
                ["Plastic Credits/yr",`${project.plastic_credits_annual?.toLocaleString()}`],
                ["Est. Carbon Revenue",fmtEur((project.carbon_credits_annual||0)*15)],
                ["Monetisation Status",project.monetisation_status],
              ].map(([l,v]) => (
                <div key={l} style={{ background:"#060f1e", borderRadius:6, padding:"7px 10px" }}>
                  <p style={{ fontSize:8, color:"#475569", marginBottom:1 }}>{l}</p>
                  <p style={{ fontSize:11, fontWeight:700, color:"#0d9488" }}>{v||"—"}</p>
                </div>
              ))}
            </div>
          )}
          {tab === "risk" && (
            <div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
                {[
                  ["Risk Rating",project.risk_rating],
                  ["ESG Category",project.esg_category],
                  ["Additionality",`${project.additionality_score}/100`],
                  ["Climate Score",`${project.climate_score}/100`],
                  ["Readiness",`${project.readiness_score}/100`],
                  ["Digital Score",`${project.digital_score}/100`],
                ].map(([l,v]) => (
                  <div key={l} style={{ background:"#060f1e", borderRadius:6, padding:"7px 10px" }}>
                    <p style={{ fontSize:8, color:"#475569", marginBottom:1 }}>{l}</p>
                    <p style={{ fontSize:11, fontWeight:700, color:riskColor(project.risk_rating) }}>{v}</p>
                  </div>
                ))}
              </div>
              {[["Readiness",project.readiness_score,"#34d399"],["Climate",project.climate_score,"#0d9488"],["Additionality",project.additionality_score,"#a78bfa"]].map(([l,v,c]) => (
                <div key={l} style={{ marginBottom:8 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                    <span style={{ fontSize:10, color:"#64748b" }}>{l}</span>
                    <span style={{ fontSize:10, fontWeight:700, color:scoreColor(v) }}>{v}/100</span>
                  </div>
                  <ProgressBar value={v} color={c} height={5} showPct={false} />
                </div>
              ))}
            </div>
          )}
          {tab === "timeline" && (
            <div>
              {[
                {date:project.created_at, event:"Project submitted to pipeline", color:"#60a5fa"},
                {date:"2025-01-15", event:"Initial green/digital screening completed", color:"#a78bfa"},
                {date:"2025-03-01", event:"PFI endorsement received", color:"#f59e0b"},
                {date:"2025-05-15", event:"DBN credit assessment initiated", color:"#f97316"},
                {date:"2025-07-01", event:`Current stage: ${project.stage}`, color:"#34d399"},
              ].map((ev, i) => (
                <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                    <div style={{ width:10, height:10, borderRadius:"50%", background:ev.color, flexShrink:0 }} />
                    {i < 4 && <div style={{ width:1, flex:1, background:"#0f2033", minHeight:20 }} />}
                  </div>
                  <div style={{ paddingBottom:8 }}>
                    <p style={{ fontSize:9, color:"#475569", marginBottom:1 }}>{(ev.date||"").slice(0,10)||"—"}</p>
                    <p style={{ fontSize:10, color:"#94a3b8" }}>{ev.event}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DBNProjects() {
  const [projects, setProjects] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [filterSector, setFilterSector] = useState("all");
  const [filterState, setFilterState] = useState("all");
  const [filterRisk, setFilterRisk] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterStage, setFilterStage] = useState("all");
  const [sortBy, setSortBy] = useState("finance_amount");
  const [intelligenceTab, setIntelligenceTab] = useState("matrix");

  useEffect(() => {
    Promise.all([dbnApi.projects(), dbnApi.portfolioAnalytics()])
      .then(([rp, ra]) => { setProjects(rp.data?.projects || []); setAnalytics(ra.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
    dbnApi.project("map").catch(() => {});
    import("@/api/client").then(({ default: cl }) =>
      cl.get("/dbn/map-data").then(r => setMapData(r.data?.features || [])).catch(() => {})
    );
  }, []);

  if (loading) return <LoadingState />;

  const sectors = [...new Set(projects.map(p => p.sector))];
  const states  = [...new Set(projects.map(p => p.state))].sort();
  const stages  = [...new Set(projects.map(p => p.stage))];

  const filtered = projects
    .filter(p =>
      (!searchQ || p.name.toLowerCase().includes(searchQ.toLowerCase()) || p.sponsor.toLowerCase().includes(searchQ.toLowerCase())) &&
      (filterSector === "all" || p.sector === filterSector) &&
      (filterState  === "all" || p.state  === filterState)  &&
      (filterRisk   === "all" || p.risk_rating === filterRisk) &&
      (filterStage  === "all" || p.stage === filterStage) &&
      (filterType   === "all" ||
        (filterType === "green" && p.green_eligible) ||
        (filterType === "digital" && p.digital_eligible) ||
        (filterType === "inclusion" && p.inclusion_eligible))
    )
    .sort((a,b) => sortBy === "readiness" ? b.readiness_score - a.readiness_score :
                   sortBy === "irr"       ? b.irr - a.irr : b.finance_amount - a.finance_amount);

  const totalFinance = filtered.reduce((a, p) => a + p.finance_amount, 0);
  const totalJobs    = filtered.reduce((a, p) => a + p.jobs_created, 0);
  const totalEmissions = filtered.reduce((a, p) => a + p.emissions_avoided_tco2e, 0);

  const bubbleData = (analytics?.impact_risk_matrix || []).map(d => ({
    ...d,
    fill: d.color,
  }));

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PageHeader
        title="Project Portfolio"
        subtitle={`${projects.length} projects · ${fmtEur(projects.reduce((a,p)=>a+p.finance_amount,0))} total · 22 states`}>
        <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
          style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:8,
            padding:"4px 8px", fontSize:10, color:"#94a3b8" }}>
          <option value="finance_amount">Sort: Finance</option>
          <option value="readiness">Sort: Readiness</option>
          <option value="irr">Sort: IRR</option>
        </select>
      </PageHeader>

      <div style={{ padding:"16px 24px", flex:1 }}>
        {/* ── EXECUTIVE PORTFOLIO INTELLIGENCE ── */}
        <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16, marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
            <SectionHeader title="Executive Portfolio Intelligence" sub="Impact · Risk · Exposure · Watchlist · AI Insights" />
            <div style={{ display:"flex", gap:2 }}>
              {[
                {key:"matrix",   label:"Impact/Risk Matrix"},
                {key:"treemap",  label:"Exposure Treemap"},
                {key:"map",      label:"Geographic View"},
                {key:"watchlist",label:"Watchlist"},
                {key:"insights", label:"AI Insights"},
              ].map(t => (
                <button key={t.key} onClick={() => setIntelligenceTab(t.key)}
                  style={{ padding:"5px 10px", borderRadius:7, border:"none", cursor:"pointer", fontSize:10, fontWeight:700,
                    background:intelligenceTab===t.key?"rgba(52,211,153,0.15)":"transparent",
                    color:intelligenceTab===t.key?"#34d399":"#475569", transition:"all .15s" }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Impact vs Risk Matrix */}
          {intelligenceTab === "matrix" && (
            <div>
              <p style={{ fontSize:9, color:"#334155", marginBottom:10 }}>
                X-axis = Risk Score (higher = riskier) · Y-axis = Development Impact Score · Bubble Size = Finance Amount · Colour = Project Type
              </p>
              <div style={{ display:"flex", gap:6, marginBottom:8 }}>
                {[["Green & Digital","#34d399"],["Digital","#60a5fa"],["Green","#a78bfa"],["General","#f59e0b"]].map(([l,c]) => (
                  <div key={l} style={{ display:"flex", alignItems:"center", gap:4 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:c }} />
                    <span style={{ fontSize:9, color:"#475569" }}>{l}</span>
                  </div>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <ScatterChart margin={{ top:10, right:20, bottom:20, left:20 }}>
                  <CartesianGrid stroke="#0f2033" strokeDasharray="3 3" />
                  <XAxis dataKey="x" name="Risk" domain={[0,100]} type="number"
                    label={{ value:"Risk", position:"insideBottomRight", offset:-10, fill:"#475569", fontSize:9 }}
                    tick={{ fill:"#475569", fontSize:9 }} />
                  <YAxis dataKey="y" name="Dev Impact" domain={[0,100]} type="number"
                    label={{ value:"Dev Impact", angle:-90, position:"insideLeft", fill:"#475569", fontSize:9 }}
                    tick={{ fill:"#475569", fontSize:9 }} />
                  <ZAxis dataKey="z" range={[30, 300]} />
                  <RTooltip content={<BubbleTooltip />} />
                  <Scatter data={bubbleData} onClick={(d) => {
                    const proj = projects.find(p => p.id === d.id);
                    if (proj) setSelected(proj);
                  }}>
                    {bubbleData.map((d, i) => (
                      <Cell key={i} fill={d.color} fillOpacity={0.75} stroke={d.color} strokeWidth={1} cursor="pointer" />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
              <p style={{ fontSize:8, color:"#1e3a5f", textAlign:"center", marginTop:4 }}>Click any bubble to open the project workspace</p>
            </div>
          )}

          {/* Treemap */}
          {intelligenceTab === "treemap" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
              <div>
                <p style={{ fontSize:10, fontWeight:700, color:"#64748b", marginBottom:8 }}>Portfolio Exposure by Sector</p>
                <SimpleTreemap data={analytics?.treemap_sectors || []} height={140} />
                <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginTop:8 }}>
                  {(analytics?.treemap_sectors||[]).map(s => (
                    <div key={s.sector} style={{ display:"flex", alignItems:"center", gap:3 }}>
                      <div style={{ width:6, height:6, borderRadius:1, background:SECTOR_COLORS[s.sector]||"#475569" }} />
                      <span style={{ fontSize:8, color:"#475569" }}>{s.sector.split(" ")[0]} {fmtEur(s.finance)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p style={{ fontSize:10, fontWeight:700, color:"#64748b", marginBottom:8 }}>Portfolio Exposure by State</p>
                <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                  {(analytics?.treemap_states||[]).slice(0,8).map(s => (
                    <div key={s.state} style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:9, color:"#94a3b8", width:80, flexShrink:0 }}>{s.state}</span>
                      <div style={{ flex:1, height:6, background:"#0f2033", borderRadius:3 }}>
                        <div style={{ height:6, width:`${(s.finance / (analytics?.treemap_states?.[0]?.finance||1)) * 100}%`,
                          background:"linear-gradient(90deg,#34d399,#0d9488)", borderRadius:3 }} />
                      </div>
                      <span style={{ fontSize:9, color:"#64748b", width:40, textAlign:"right" }}>{fmtEur(s.finance)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Geographic Map */}
          {intelligenceTab === "map" && (
            <div>
              <p style={{ fontSize:9, color:"#334155", marginBottom:8 }}>
                Projects coloured by type: Green (teal) · Digital (blue) · Green &amp; Digital (mixed) · Inclusion (amber)
              </p>
              <DBNNigeriaMap features={mapData} colorBy="type" height={320} />
            </div>
          )}

          {/* Executive Watchlist */}
          {intelligenceTab === "watchlist" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div>
                <p style={{ fontSize:10, fontWeight:700, color:"#64748b", marginBottom:8 }}>Auto-Surfaced Projects Requiring Attention</p>
                {(analytics?.watchlist || []).map((item, i) => (
                  <WatchlistCard key={i} item={item} onOpen={(w) => {
                    const proj = projects.find(p => p.id === w.id);
                    if (proj) setSelected(proj);
                  }} />
                ))}
              </div>
              <div>
                <p style={{ fontSize:10, fontWeight:700, color:"#64748b", marginBottom:8 }}>Portfolio Summary</p>
                {[
                  {l:"Total Portfolio Finance",   v:fmtEur(analytics?.summary?.total_finance),    c:"#34d399"},
                  {l:"Active Projects",           v:analytics?.summary?.active,                    c:"#60a5fa"},
                  {l:"In Pipeline",               v:analytics?.summary?.in_pipeline,              c:"#a78bfa"},
                  {l:"High-Risk Projects",        v:analytics?.summary?.high_risk,                c:"#ef4444"},
                  {l:"Largest Sector",            v:analytics?.summary?.largest_sector,           c:"#f59e0b"},
                  {l:"Most Financed State",       v:analytics?.summary?.largest_state,            c:"#0891b2"},
                ].map(x => (
                  <div key={x.l} style={{ display:"flex", justifyContent:"space-between",
                    padding:"6px 0", borderBottom:"1px solid #0f1e33" }}>
                    <span style={{ fontSize:10, color:"#475569" }}>{x.l}</span>
                    <span style={{ fontSize:10, fontWeight:700, color:x.c }}>{x.v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Insights */}
          {intelligenceTab === "insights" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              {(analytics?.ai_observations || []).map((obs, i) => (
                <div key={i} style={{ background:"#060f1e", borderRadius:10, padding:"12px 14px",
                  borderLeft:`3px solid ${obs.color}` }}>
                  <p style={{ fontSize:11, fontWeight:700, color:"#f0f9ff", marginBottom:6 }}>{obs.title}</p>
                  <p style={{ fontSize:9.5, color:"#64748b", lineHeight:1.6 }}>{obs.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── QUICK STATS ── */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
          {[
            { l:"Filtered Projects",  v:filtered.length,          c:"#60a5fa" },
            { l:"Total Finance",      v:fmtEur(totalFinance),     c:"#34d399" },
            { l:"Jobs Created",       v:fmtN(totalJobs),          c:"#fbbf24" },
            { l:"Emissions Avoided",  v:`${fmtN(totalEmissions)} tCO₂e`, c:"#0d9488" },
          ].map(x => (
            <div key={x.l} style={{ background:"#0a1628", border:"1px solid #0f2033",
              borderTop:`3px solid ${x.c}`, borderRadius:10, padding:"10px 12px" }}>
              <p style={{ fontSize:18, fontWeight:900, color:x.c }}>{x.v}</p>
              <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.08em" }}>{x.l}</p>
            </div>
          ))}
        </div>

        {/* ── FILTERS ── */}
        <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, background:"#0a1628",
            border:"1px solid #0f2033", borderRadius:8, padding:"5px 10px" }}>
            <Search size={11} color="#475569" />
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
              placeholder="Search projects or sponsors…"
              style={{ background:"none", border:"none", color:"#f0f9ff", fontSize:10, outline:"none", width:180 }} />
          </div>
          {[
            { val:filterSector, set:setFilterSector, opts:[{v:"all",l:"All Sectors"},...sectors.map(s=>({v:s,l:s}))] },
            { val:filterState,  set:setFilterState,  opts:[{v:"all",l:"All States"},...states.map(s=>({v:s,l:s}))] },
            { val:filterRisk,   set:setFilterRisk,   opts:[{v:"all",l:"All Risk"},{v:"Low",l:"Low"},{v:"Moderate",l:"Moderate"},{v:"High",l:"High"}] },
            { val:filterStage,  set:setFilterStage,  opts:[{v:"all",l:"All Stages"},...stages.map(s=>({v:s,l:s}))] },
            { val:filterType,   set:setFilterType,   opts:[{v:"all",l:"All Types"},{v:"green",l:"Green"},{v:"digital",l:"Digital"},{v:"inclusion",l:"Inclusion"}] },
          ].map((f, i) => (
            <select key={i} value={f.val} onChange={e => f.set(e.target.value)}
              style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:8,
                padding:"5px 10px", fontSize:10, color:"#94a3b8" }}>
              {f.opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          ))}
          <span style={{ fontSize:10, color:"#334155" }}>{filtered.length} results</span>
        </div>

        {/* ── PROJECT TABLE ── */}
        <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr>
                {["Project","Sector","State","Finance","Stage","Risk","Score","Type"].map(h => (
                  <th key={h} style={{ background:"#060f1e", color:"#475569", fontSize:8.5, fontWeight:700,
                    padding:"8px 10px", textAlign:"left", textTransform:"uppercase", letterSpacing:"0.07em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <ProjectRow key={p.id} project={p} onClick={setSelected} selected={selected?.id===p.id} />
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div style={{ textAlign:"center", padding:"24px 0", color:"#334155", fontSize:11 }}>
              No projects match the current filters
            </div>
          )}
        </div>
      </div>

      {selected && <ProjectDetail project={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
